import { ActionButton, useConfirmDialog, useSnackbar, type Tone } from '@if/mui'
import { DevToolsCommandType } from '@src/types/dev-tools'
import { useTranslation } from 'react-i18next'
import CommandCard from './CommandCard'

interface AdminCommandCardProps {
  title: string
  description: string
  actionLabel: string
  confirmationMessage: string
  onExecute: () => Promise<void>
  isPending: boolean
  commandType: DevToolsCommandType
  /** What the command represents — `warning` for one that stops work in flight. */
  tone?: Tone
}

/** A command that takes no input: confirm, fire, report. */
const AdminCommandCard = ({
  title,
  description,
  actionLabel,
  confirmationMessage,
  onExecute,
  isPending,
  commandType,
  tone,
}: AdminCommandCardProps) => {
  const { t } = useTranslation()
  const { showSuccess, showError } = useSnackbar()

  const confirm = useConfirmDialog({
    title: t('admin.devTools.dialog.title'),
    message: confirmationMessage,
    confirmLabel: actionLabel,
    confirmTone: tone,
  })

  const handleRun = async () => {
    if (!(await confirm.open())) return
    try {
      await onExecute()
    } catch {
      showError(t('admin.devTools.messages.error'))
      return
    }
    showSuccess(t('admin.devTools.messages.success'))
  }

  return (
    <CommandCard
      title={title}
      description={description}
      commandType={commandType}
      actions={
        <ActionButton emphasis="principal" tone={tone} loading={isPending} onClick={() => void handleRun()}>
          {actionLabel}
        </ActionButton>
      }
    />
  )
}

export default AdminCommandCard
